import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Star,
  CheckCircle2,
  Globe,
  Bot,
  Smartphone,
  BarChart3,
} from "lucide-react";
import HeroChart from "./HerChart";

function Hero() {
  const chartData = [40, 65, 90, 120, 150, 180, 220];
  return (
    <section className="relative overflow-hidden bg-[#050816] text-white">

      {/* ===================== Background ===================== */}

      {/* Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.04)_1px,transparent_1px)] bg-[size:60px_60px]" />

      {/* Aurora Glow */}
      <motion.div
        animate={{
          x: [0, 80, 0],
          y: [0, 40, 0],
        }}
        transition={{
          repeat: Infinity,
          duration: 18,
          ease: "easeInOut",
        }}
        className="absolute -left-32 -top-40 h-[500px] w-[500px] rounded-full bg-violet-600/30 blur-[130px]"
      />

      <motion.div
        animate={{
          x: [0, -70, 0],
          y: [0, -50, 0],
        }}
        transition={{
          repeat: Infinity,
          duration: 20,
          ease: "easeInOut",
        }}
        className="absolute right-0 top-20 h-[450px] w-[450px] rounded-full bg-cyan-500/20 blur-[120px]"
      />

      <motion.div
        animate={{
          scale: [1, 1.15, 1],
        }}
        transition={{
          repeat: Infinity,
          duration: 10,
        }}
        className="absolute bottom-0 left-1/2 h-[350px] w-[350px] -translate-x-1/2 rounded-full bg-pink-500/20 blur-[120px]"
      />

      {/* Noise */}
      <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle,white_1px,transparent_1px)] bg-[length:18px_18px]" />

      {/* ===================== Hero ===================== */}

      <div className="relative z-10 mx-auto max-w-6xl pt-10 px-4 sm:px-6 lg:px-8 md:pt-10 pb-14 sm:pb-20 lg:pb-16">

        <div className="grid items-center gap-10 lg:gap-16 lg:grid-cols-2">

          {/* ================= Left ================= */}

          <div>

            {/* Badge */}

            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 sm:px-5 sm:py-2 backdrop-blur-xl"
            >
              <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-400" />

              <span className="text-[11px] sm:text-sm text-gray-200">
                Trusted by Growing Businesses
              </span>

            </motion.div>

            {/* Heading */}

            <motion.h1
              initial={{
                opacity: 0,
                y: 40,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: .15,
              }}
              className="mt-5 sm:mt-7 lg:mt-8 text-4xl sm:text-5xl font-black leading-tight"
            >
              Build.

              <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
                {" "}Automate.
              </span>

              <br />

              Scale Your

              <br />

              Business.
            </motion.h1>

            {/* Description */}

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: .35 }}
              className="mt-2 sm:mt-6 lg:mt-8 max-w-xl text-sm sm:text-base lg:text-l leading-6 sm:leading-7 lg:leading-8 text-gray-300"
            >
              GrowX builds premium websites,
              AI automation,
              CRM solutions,
              mobile apps and digital marketing
              that help businesses generate more leads,
              increase revenue and grow faster.
            </motion.p>

            {/* CTA */}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: .55 }}
              className="mt-6 sm:mt-8 lg:mt-10 flex flex-wrap gap-3 sm:gap-4"
            >

              <a
                href="#contact"
              className="group inline-flex items-center gap-2 rounded-xl sm:rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-5 py-3 sm:px-8 sm:py-4 text-sm sm:text-base font-semibold shadow-[0_15px_45px_rgba(124,58,237,.45)] transition duration-300 hover:scale-105"
              >
                Book Free Consultation

                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition group-hover:translate-x-1" />

              </a>

              <a
                href="#projects"
               className="rounded-xl sm:rounded-2xl border border-white/10 bg-white/5 px-5 py-3 sm:px-8 sm:py-4 text-sm sm:text-base font-semibold backdrop-blur-xl transition hover:bg-white hover:text-black"
              >
                View Portfolio
              </a>

            </motion.div>

            {/* Services */}

            <div className="mt-6 sm:mt-8 lg:mt-10 flex flex-wrap gap-2 sm:gap-4">

              {[
                {
                  icon: Globe,
                  text: "Web Development",
                },
                {
                  icon: Bot,
                  text: "AI Automation",
                },
                {
                  icon: Smartphone,
                  text: "Mobile Apps",
                },
                {
                  icon: BarChart3,
                  text: "Digital Marketing",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 rounded-lg sm:rounded-xl border border-white/10 bg-white/5 px-3 py-2 sm:px-4 sm:py-3 backdrop-blur-xl"
                >
                  <item.icon
                    size={18}
                   className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-violet-300"
                  />

                  <span className="text-xs sm:text-sm">
                    {item.text}
                  </span>
                </div>
              ))}

            </div>

          </div>

          {/* ================= Right ================= */}
        
          <motion.div
            initial={{
              opacity: 0,
              x: 60,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: .8,
            }}
          className="relative w-full flex justify-center lg:justify-end px-4 sm:px-0"
          >
            {/* ================= Premium Dashboard ================= */}

            <motion.div
              animate={{
                y: [0, -12, 0],
              }}
              transition={{
                repeat: Infinity,
                duration: 6,
                ease: "easeInOut",
              }}
             className="relative w-full max-w-[340px] sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl rounded-[24px] sm:rounded-[30px] border border-white/10 bg-white/5 backdrop-blur-2xl shadow-[0_30px_80px_rgba(0,0,0,.45)] overflow-hidden p-4 sm:p-6 lg:p-8 mx-auto"
            >
                {/* Analytics */}
                <HeroChart/>
              
             
            </motion.div>

            {/* Floating Cards start in Part 3 */}
          </motion.div>

        </div>

      </div>

            
          

    </section>
  );
}

export default Hero;
